import React from "react";
import { observer } from "mobx-react-lite";

import type { ViewComponent } from "@frui.ts/views";
import FilterView from "views/common/filter";

import { Input } from "@frui.ts/bootstrap";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import type PaymentCardGatewayViewModel from "viewModels/finance/payments/cardGateWay/paymentCardGatewayViewModel";
import NumberRangeInputControl from "controls/numberRangeInput";
import DateRangeInputControl from "controls/dateRangeInput";
import { FormLabelWithTooltip } from "controls/formLabelWithTooltip";
import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";
import Button from "@edaz/edazkit/Button";
import { IconType } from "@edaz/edazkit/Icon";
import { isFilterDirty, isFilterEmpty } from "utils/filters";
import AdvancedInput from "@edaz/edazkit/controls/AdvancedInput";

const PaymentCardGatewayFilters: ViewComponent<PaymentCardGatewayViewModel> = observer(({ vm }) => {
  const customFilterButton = (
    <Button iconLeft={IconType.Search} className="btn-form-height" type="submit" disabled={!isFilterDirty(vm.filter)}>
      {vm.translateFilter("customFilter")}
    </Button>
  );
  const customResetButton =
    isFilterDirty(vm.filter) || !isFilterEmpty(vm.filter) ? (
      <Button iconLeft={IconType.Cross} variant="outline-danger" className="btn-form-height" type="reset">
        {vm.translateFilter("customResetFilter")}
      </Button>
    ) : (
      <></>
    );
  const regexOnlyNumbers = /[^0-9]+/g;
  const regexNumbersChars = /[^0-9A-Za-z]+/g;

  return (
    <FilterView vm={vm} customResetButton={customResetButton} customFilterButton={customFilterButton}>
      <div className="row">
        <Form.Group as={Col}>
          <div className="d-flex flex-row">
            <Form.Group as={Col}>
              <FormLabelWithTooltip tooltipText={vm.translateFilter("cardNumber.firstPartTooltip")}>
                {vm.translateFilter("cardNumber.firstPart")}
              </FormLabelWithTooltip>
              <AdvancedInput
                target={vm.filter}
                property="_cardNumberFirst"
                placeholder={vm.translatePlaceholder("cardNumber.firstPart")}
                ignoredCharacters={regexOnlyNumbers}
              />
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label> {vm.translateFilter("cardNumber.secondPart")}</Form.Label>
              <AdvancedInput
                target={vm.filter}
                property="_cardNumberSecond"
                placeholder={vm.translatePlaceholder("cardNumber.secondPart")}
                ignoredCharacters={regexOnlyNumbers}
              />
            </Form.Group>
            <Form.Group as={Col} md={3}>
              <Form.Label>{vm.translateFilter("variableSymbol")}</Form.Label>
              <AdvancedInput
                target={vm.filter}
                property="variableSymbol"
                placeholder={vm.translatePlaceholder("variableSymbol")}
                ignoredCharacters={regexOnlyNumbers}
              />
            </Form.Group>
            <Form.Group as={Col} md={6}>
              <div className="d-flex flex-row">
                <div style={{ flex: "1" }}>
                  <Form.Label>{vm.translateFilter("statementDate.from")}</Form.Label>
                </div>
                <div style={{ flex: "1", marginLeft: "30px" }}>
                  <Form.Label>{vm.translateFilter("statementDate.to")}</Form.Label>
                </div>
              </div>
              <div>
                <DateRangeInputControl target={vm.filter} property="statementDate" customPlaceholder="paymentsStatementDate" />
              </div>
            </Form.Group>
          </div>
          <div className="d-flex flex-row">
            <Form.Group as={Col} md={3}>
              <div className="d-flex flex-row">
                <div style={{ flex: "1" }}>
                  <FormLabelWithTooltip tooltipText={vm.translateFilter("amount.fromTooltip")}>
                    {vm.translateFilter("amount.from")}
                  </FormLabelWithTooltip>
                </div>
                <div style={{ flex: "1", marginLeft: "30px" }}>
                  <Form.Label>{vm.translateFilter("amount.to")}</Form.Label>
                </div>
              </div>
              <div>
                <NumberRangeInputControl
                  target={vm.filter}
                  property="amount"
                  customPlaceholder="paymentsAmount"
                  step={0.01}
                  min={0}
                />
              </div>
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>{vm.translateFilter("terminalType")}</Form.Label>
              <AdvancedSelect
                target={vm.filter}
                property="terminalType"
                allowEmpty={true}
                disabled={vm.busyWatcher.isBusy}
                items={vm.terminalTypes}
                placeholder={vm.translatePlaceholder("terminalType")}
              />
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>{vm.translateFilter("authCode")}</Form.Label>
              <AdvancedInput
                target={vm.filter}
                property="authCode"
                placeholder={vm.translatePlaceholder("authCode")}
                ignoredCharacters={regexNumbersChars}
              />
            </Form.Group>
            <Form.Group as={Col}>
              <Form.Label>{vm.translateFilter("paymentIdent")}</Form.Label>
              <Input target={vm.filter} property="paymentIdent" placeholder={vm.translatePlaceholder("paymentIdent")} />
            </Form.Group>
            <Form.Group as={Col} md={3}>
              <Form.Label>{vm.translateFilter("arn")}</Form.Label>
              <AdvancedInput
                target={vm.filter}
                property="arn"
                placeholder={vm.translatePlaceholder("arn")}
                ignoredCharacters={regexNumbersChars}
              />
            </Form.Group>
          </div>
        </Form.Group>
      </div>
    </FilterView>
  );
});

export default PaymentCardGatewayFilters;
